import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import './index.css';
import { ApiError } from './lib/api';
import { AuthProvider, useAuth } from './lib/auth';
import { Login } from './pages/Login';

// Cada sección se descarga recién cuando se abre.
const ComplaintDrawer = lazy(() => import('./pages/ComplaintDrawer').then((m) => ({ default: m.ComplaintDrawer })));
const Complaints = lazy(() => import('./pages/Complaints').then((m) => ({ default: m.Complaints })));
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Licencia = lazy(() => import('./pages/Licencia').then((m) => ({ default: m.Licencia })));
const MfaSetup = lazy(() => import('./pages/Login').then((m) => ({ default: m.MfaSetup })));
const Posts = lazy(() => import('./pages/Posts').then((m) => ({ default: m.Posts })));
const PostEditor = lazy(() => import('./pages/PostEditor').then((m) => ({ default: m.PostEditor })));
const Alerts = lazy(() => import('./pages/Alerts').then((m) => ({ default: m.Alerts })));
const AlertForm = lazy(() => import('./pages/AlertForm').then((m) => ({ default: m.AlertForm })));
const Users = lazy(() => import('./pages/Users').then((m) => ({ default: m.Users })));
const UserDetail = lazy(() => import('./pages/UserDetail').then((m) => ({ default: m.UserDetail })));
const InviteStaff = lazy(() => import('./pages/UserDetail').then((m) => ({ default: m.InviteStaff })));
const Roles = lazy(() => import('./pages/Roles').then((m) => ({ default: m.Roles })));
const Departments = lazy(() => import('./pages/Departments').then((m) => ({ default: m.Departments })));
const ActivatePassword = lazy(() => import('./pages/Password').then((m) => ({ default: m.ActivatePassword })));
const ForgotPassword = lazy(() => import('./pages/Password').then((m) => ({ default: m.ForgotPassword })));
const Directory = lazy(() => import('./pages/guide/Directory').then((m) => ({ default: m.Directory })));
const Pharmacies = lazy(() => import('./pages/guide/Pharmacies').then((m) => ({ default: m.Pharmacies })));
const Procedures = lazy(() => import('./pages/guide/Procedures').then((m) => ({ default: m.Procedures })));
const Events = lazy(() => import('./pages/guide/Events').then((m) => ({ default: m.Events })));
const Links = lazy(() => import('./pages/guide/Links').then((m) => ({ default: m.Links })));
const NewComplaint = lazy(() => import('./pages/NewComplaint').then((m) => ({ default: m.NewComplaint })));
import { AdminLayout, NAV } from './ui/Layout';
import { EmptyState, Spinner } from './ui/kit';

/**
 * Una sola app para dos dominios: admin.* (panel general) y licencia.* (oficina
 * de licencias). En desarrollo, /licencia muestra el área de licencias.
 */
const isLicenciaHost = window.location.hostname.startsWith('licencia.');

function Gate({ area, children }: { area: string; children: React.ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') return <Spinner />;
  if (status === 'anonymous') return <Login area={area} />;
  return <Suspense fallback={<Spinner />}>{status === 'needs-mfa' ? <MfaSetup /> : children}</Suspense>;
}

/** El inicio depende del rol: el tablero de reclamos o la primera sección habilitada. */
function Home() {
  const { can } = useAuth();
  if (can('complaints.view')) return <Dashboard />;
  const first = NAV.find((n) => can(n.permission));
  return first ? <Navigate to={first.to} replace /> : <EmptyState title="Tu cuenta no tiene secciones habilitadas" text="Pedile a un administrador que te asigne un rol." />;
}

function App() {
  // Los enlaces de contraseña funcionan en los dos dominios.
  if (isLicenciaHost && !['/activar', '/recuperar'].includes(window.location.pathname)) return <Gate area="Licencias"><Licencia /></Gate>;
  return (
    <Routes>
      <Route path="/licencia/*" element={<Gate area="Licencias"><Licencia /></Gate>} />
      <Route path="/activar" element={<Suspense fallback={<Spinner />}><ActivatePassword /></Suspense>} />
      <Route path="/recuperar" element={<Suspense fallback={<Spinner />}><ForgotPassword /></Suspense>} />
      <Route element={<Gate area="Panel"><AdminLayout /></Gate>}>
        <Route index element={<Home />} />
        <Route path="publicaciones" element={<Posts />} />
        <Route path="publicaciones/nueva" element={<PostEditor />} />
        <Route path="publicaciones/:id" element={<PostEditor />} />
        <Route path="alertas" element={<Alerts />} />
        <Route path="alertas/nueva" element={<AlertForm />} />
        <Route path="agenda" element={<Events />} />
        <Route path="farmacias" element={<Pharmacies />} />
        <Route path="telefonos" element={<Directory />} />
        <Route path="tramites" element={<Procedures />} />
        <Route path="enlaces" element={<Links />} />
        <Route path="usuarios" element={<Users />} />
        <Route path="usuarios/invitar" element={<InviteStaff />} />
        <Route path="usuarios/:id" element={<UserDetail />} />
        <Route path="roles" element={<Roles />} />
        <Route path="areas" element={<Departments />} />
        <Route path="reclamos/nuevo" element={<NewComplaint />} />
        <Route path="reclamos" element={<Complaints />}>
          <Route path=":id" element={<ComplaintDrawer />} />
        </Route>
        <Route path="*" element={<EmptyState title="Página no encontrada" />} />
      </Route>
    </Routes>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (n, e) => n < 2 && !(e instanceof ApiError && e.status >= 400 && e.status < 500),
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
